import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service, Slack } from 'webapp-types';
import { DeliveryType, SlackMessage } from 'webapp/enums/enums';
import { equal, isAlive } from 'webapp/utils/common';

import type { CheckboxItemInfo } from 'webapp/components/form/checkbox';
import FormCheckboxGroupComponent from 'webapp/components/form/checkbox-group';
import type { GroupSelected } from 'webapp/components/form/checkbox-group';
import NotificationsSlackPopupSelectListComponent from 'webapp/components/notifications/slack/popup-select-list';

interface Args {
	selected: DeliveryType[];
	slackChannels: Slack.Channels;
	onSelectionChange: (value: DeliveryType[], isFromSetDefaults?: boolean) => void;
	setDefaults?: boolean;
	isDisabled?: boolean;
	isEmailEnabled?: boolean;
	deliveryTypes?: DeliveryType[];
	isMobileComingSoon?: boolean;
	isSlackComingSoon?: boolean;
	hideLabel?: boolean;
	addSendToTextToLabels?: boolean;
	extraClass?: string;
	theme?: string;
	onSlackChannelsChange?: (value: Slack.Channels, isFromSetDefaults?: boolean) => void;
}

interface NotificationsDeliveryTargetsComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class NotificationsDeliveryTargetsComponent extends Component<
	NotificationsDeliveryTargetsComponentInterface<Args>
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('constants') declare constantsService: Service.Constants;

	defaultDeliveryTypes = [DeliveryType.PUSH, DeliveryType.EMAIL, DeliveryType.SLACK];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// setting default delivery for new scorecards
		if (this.args.setDefaults) {
			const shownOptions = this.options;

			let delivery = (this.args.selected || []).length
				? Array.from(this.args.selected)
				: [DeliveryType.PUSH, DeliveryType.EMAIL]; // mobile & email by default

			delivery = delivery.filter((deliveryId) => {
				const obj = shownOptions.find((item) => item.value === deliveryId);
				return obj && !obj.disabled;
			});

			const deliveryType = shownOptions
				.filter((item) => !item.disabled)
				.map((item) => item.value)[0];
			if (deliveryType && !delivery.length) {
				delivery = [deliveryType as DeliveryType];
			}

			if (!equal(this.args.selected, delivery)) {
				this.onDeliveryChange(delivery, true);
			}

			this.possiblyAddSlackDirectMsgChannel(delivery, this.args.slackChannels, true);
		}
	}

	get activeUser() {
		return this.currentUserService.model; // TODO: when ts add cast `as User`
	}

	get hasMobileApp() {
		return !!this.activeUser?.lastLoginMobile;
	}

	get isSlackConnected() {
		return this.currentUserService.isSlackConnected;
	}

	get isSlackEnabled() {
		return this.currentUserService.isSlackEnabled;
	}

	get isEmailEnabled() {
		return this.args.isEmailEnabled ?? !!this.activeUser?.notifications.includes(16);
	}

	get isMobileComingSoon() {
		return !!this.args.isMobileComingSoon;
	}

	get isSlackComingSoon() {
		return !!this.args.isSlackComingSoon;
	}

	get isSlackDirectMessage() {
		return !!(this.args.slackChannels || []).find((item) => item.id === SlackMessage.DIRECT);
	}

	get slackChannelsLength() {
		return this.args.slackChannels.length;
	}

	get hideLabel() {
		return !!this.args.hideLabel;
	}

	get slackChannelsText() {
		const slackChannels = this.args.slackChannels || [];

		let text = '';

		const channels = slackChannels.map((channel) => {
			return `#${(channel as Slack.Channel).name}`;
		});

		text = channels.join('<br/>') + text;

		const firstChannel = slackChannels[0]!;
		if (text && 'workspace' in firstChannel) {
			text += ` (${firstChannel.workspace})`;
		}

		return text;
	}

	get options() {
		const hideDataboxContent =
				this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			showSlackIntegration = this.accountService.showSlackIntegration,
			addSendToTextToLabels = !!this.args.addSendToTextToLabels;
		const deliveryTypes = this.args.deliveryTypes || this.defaultDeliveryTypes;
		return this.constantsService.eventDeliveryTypes
			.filter((deliveryType) => {
				return deliveryTypes.includes(deliveryType.value);
			})
			.map((deliveryType) => {
				let label = deliveryType.label;
				if (addSendToTextToLabels) {
					label = `Send to ${label}`;
				}
				const optionData: CheckboxItemInfo = {
					value: deliveryType.value,
					label,
				};

				switch (deliveryType.value) {
					case DeliveryType.PUSH:
						if (this.isMobileComingSoon) {
							optionData.disabled = true;
							optionData.label += ' (soon)';
						} else if (!this.hasMobileApp) {
							optionData.disabled = true;
							if (!hideDataboxContent) {
								optionData.link = {
									label: 'Download',
									action: function () {
										window.open('https://databox.com/download', '_blank');
									},
								};
							}
						}
						break;
					case DeliveryType.EMAIL:
						if (!this.isEmailEnabled) {
							optionData.disabled = true;
							optionData.tooltip =
								'You have email delivery disabled in your profile settings. To turn it on, go under “My profile”.';
						}
						break;
					case DeliveryType.SLACK:
						if (this.isSlackComingSoon) {
							optionData.disabled = true;
							optionData.label += ' (soon)';
						} else if (!this.isSlackConnected) {
							optionData.disabled = true;

							if (showSlackIntegration) {
								optionData.link = {
									label: 'Connect Slack',
									action: () => {
										this.currentUserService.openSlackAuthorizationPopup();
									},
								};
							}
						} else if (!this.isSlackEnabled) {
							optionData.disabled = true;
							optionData.tooltip =
								'You have Slack notification delivery disabled in your profile settings. To turn it on, go under “My profile”.';
						} else if ((this.args.selected || []).includes(DeliveryType.SLACK)) {
							// only if slack is enabled
							optionData.link = {
								label: 'Set channels',
								action: () => {
									this.openSlackChannelList();
								},
							};

							if (this.isSlackDirectMessage) {
								optionData.tooltipIcon = 'InfoFill';
								optionData.tooltip = 'Delivered as direct message';
							} else if (this.slackChannelsText) {
								// aleady have some channels
								optionData.used = `${this.slackChannelsLength} channel(s)`;
								optionData.usedTooltip = this.slackChannelsText;
								optionData.link.label = 'Change';
							}
						}
						break;
				}

				return optionData;
			});
	}

	openSlackChannelList() {
		this.popupService
			.createModal(NotificationsSlackPopupSelectListComponent, {
				popupClass: 'popup-type-notification-slack-list',
			})
			.open({
				selectedChannelsIds: (this.args.slackChannels || []).map((item) => item.id),
				onConfirm: (selectedChannels: Slack.Channel[]) => {
					if (isAlive(this)) {
						// change the whole object because isDirty sucks with arrays
						const { onSlackChannelsChange } = this.args;
						if (onSlackChannelsChange) {
							onSlackChannelsChange(selectedChannels);
						}
						this.possiblyAddSlackDirectMsgChannel(this.args.selected, selectedChannels);
					}
				},
			});
	}

	possiblyAddSlackDirectMsgChannel(
		selected: DeliveryType[],
		slackChannels?: Slack.Channels,
		isFromSetDefaults = false,
	) {
		if (selected.includes(DeliveryType.SLACK) && slackChannels?.length === 0) {
			// if slack selected, but no channels selected, means it's direct msg
			const { onSlackChannelsChange } = this.args;
			if (onSlackChannelsChange) {
				onSlackChannelsChange([{ id: SlackMessage.DIRECT }], isFromSetDefaults);
			}
		}
	}

	@action
	onDeliveryChange(value: GroupSelected, isFromSetDefaults = false) {
		const deliveryValue = value as DeliveryType[];
		this.args.onSelectionChange(deliveryValue, isFromSetDefaults);
		this.possiblyAddSlackDirectMsgChannel(
			deliveryValue,
			this.args.slackChannels,
			isFromSetDefaults,
		);
	}

	<template>
		<div class="row row--radiobuttons delivery {{@extraClass}}">
			{{#unless this.hideLabel}}
				<label>Delivery method</label>
			{{/unless}}
			<div class={{unless this.hideLabel "right"}}>
				<FormCheckboxGroupComponent
					class="radiobutton-default"
					@name="delivery"
					@selected={{@selected}}
					@options={{this.options}}
					@isDisabled={{@isDisabled}}
					@onChange={{this.onDeliveryChange}}
					@theme={{@theme}}
				/>
				{{yield}}
			</div>
		</div>
	</template>
}
